import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  Grid,
  Tooltip,
  useTheme
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ViewModule as IndexCardIcon,
  AccountTree as MindMapIcon,
  Person as CharacterIcon,
  Public as WorldBuildingIcon,
  Description as LoglineIcon,
  Slideshow as PitchDeckIcon
} from '@mui/icons-material';

// Import individual tool components
import IndexCards from './story-tools/IndexCards';
import MindMapFlow from './story-tools/MindMapFlow';
import CharacterTool from './story-tools/CharacterTool';
import WorldBuilding from './story-tools/WorldBuilding';
import LoglineGenerator from './story-tools/LoglineGenerator';
import PitchDeck from './story-tools/PitchDeck';

const TooltippedTab = React.forwardRef(function TooltippedTab(props, ref) {
  const { tooltip, ...tabProps } = props;
  return (
    <Tooltip title={tooltip} placement="right">
      <span style={{ display: 'inline-flex', width: '100%' }}>
        <Tab ref={ref} {...tabProps} />
      </span>
    </Tooltip>
  );
});
TooltippedTab.muiName = 'Tab';

function TabPanel({ children, value, index }) {
  return (
    <div 
      hidden={value !== index} 
      style={{ 
        padding: '20px 0', 
        height: 'auto', 
        overflow: 'visible', 
        minHeight: 'auto' 
      }}
    >
      {value === index && children}
    </div>
  );
}

const StoryOutline = ({
  projectId,
  projectName = '',
  projectLogline = '',
  projectSynopsis = '',
  onWidthModeChange,
  wideMode = false
}) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (typeof onWidthModeChange === 'function') {
      onWidthModeChange(newValue === 0);
    }
  };

  const tools = [
    { label: 'Logline', icon: <LoglineIcon />, component: <LoglineGenerator projectId={projectId} /> },
    { label: 'Characters', icon: <CharacterIcon />, component: <CharacterTool projectId={projectId} /> },
    { label: 'Index Cards', icon: <IndexCardIcon />, component: <IndexCards projectId={projectId} wideMode={wideMode} onWideModeChange={onWidthModeChange} /> },
    { label: 'Mind Map', icon: <MindMapIcon />, component: <MindMapFlow projectId={projectId} /> },
    { label: 'World Building', icon: <WorldBuildingIcon />, component: <WorldBuilding projectId={projectId} /> },
    {
      label: 'Pitch Deck',
      icon: <PitchDeckIcon />,
      component: (
        <PitchDeck
          projectId={projectId}
          projectName={projectName}
          projectLogline={projectLogline}
          projectSynopsis={projectSynopsis}
        />
      )
    }
  ];

  // Do not auto-enable wide mode on initial mount; only change when user switches tabs

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="500px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="500px">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%', 
      height: '100%', 
      minHeight: '100%', 
      p: 0, 
      display: 'flex', 
      overflow: 'auto',
      backgroundColor: theme.palette.mode === 'dark' ? '#000' : theme.palette.background.default
    }}>
          <Paper 
      elevation={0} 
      sx={{ 
        display: 'flex', 
        width: '100%',
        overflow: 'auto',
        alignItems: 'stretch', 
        height: '100%',
        minHeight: '100%',
        borderRadius: 0,
        backgroundColor: theme.palette.mode === 'dark' ? '#000000' : '#fff',
      }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          orientation="vertical"
          variant="scrollable"
          scrollButtons="auto"
          sx={{ 
            borderRight: 1, 
            borderColor: theme.palette.mode === 'dark'
              ? alpha(theme.palette.divider, 0.2)
              : 'divider',
            minWidth: '56px',
            height: '100%',
            backgroundColor: theme.palette.mode === 'dark'
              ? '#1a1a1a'
              : theme.palette.background.paper,
            '& .MuiTab-root': {
              minWidth: '56px',
              minHeight: '56px',
              padding: '10px 0',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              color: theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.6)
                : theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark'
                  ? alpha(theme.palette.primary.main, 0.1)
                  : alpha(theme.palette.primary.main, 0.05),
                color: theme.palette.mode === 'dark'
                  ? alpha(theme.palette.common.white, 0.9)
                  : theme.palette.text.primary
              },
              '&.Mui-selected': {
                color: theme.palette.primary.main,
                backgroundColor: theme.palette.mode === 'dark'
                  ? alpha(theme.palette.primary.main, 0.15)
                  : alpha(theme.palette.primary.main, 0.08),
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: '20%',
                  height: '60%',
                  width: '3px',
                  backgroundColor: theme.palette.primary.main
                }
              }
            },
            '& .MuiSvgIcon-root': {
              fontSize: '22px',
              transition: 'transform 0.2s ease'
            },
            '& .MuiTab-root:hover .MuiSvgIcon-root': {
              transform: 'scale(1.1)'
            },
            '& .MuiTabs-indicator': {
              display: 'none'
            }
          }}
        >
          {tools.map((tool, index) => (
            <TooltippedTab
              key={tool.label}
              tooltip={tool.label}
              icon={tool.icon}
              id={`story-tool-tab-${index}`}
              aria-controls={`story-tool-tabpanel-${index}`}
              aria-label={tool.label}
            />
          ))}
        </Tabs>

        <Box sx={{ 
          flexGrow: 1,
          overflow: wideMode ? 'visible' : 'auto',
          maxHeight: 'none', 
          height: 'auto', 
          width: 'auto'
        }}>
          {tools.map((tool, index) => (
            <TabPanel key={tool.label} value={activeTab} index={index}>
              {tool.component}
            </TabPanel>
          ))}
        </Box>
      </Paper>
    </Box>
  );
};

export default StoryOutline;
